import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import SEO from "../components/SEO";
import AuthShell from "../components/AuthShell";
import { toast } from "sonner";
import { ArrowRight, Gift } from "lucide-react";
import PasswordInput from "../components/PasswordInput";

const SIGNUP_HERO = "https://static.prod-images.emergentagent.com/jobs/8e4196d6-aa64-413e-8d09-93aaa7923fd4/images/752ea75e3bd8f76e493b6f4a150ce1dcf71245ac8923e37daa9bf48564c29efd.png";

export default function Signup() {
    const [form, setForm] = useState({ name: "", email: "", password: "" });
    const [agreed, setAgreed] = useState(false);
    const [loading, setLoading] = useState(false);
    const { register } = useAuth();
    const nav = useNavigate();
    const [params] = useSearchParams();
    const refCode = (params.get("ref") || "").trim().toUpperCase();

    useEffect(() => {
        if (refCode) {
            try { localStorage.setItem("ef_ref", refCode); } catch (e) { /* ignore */ }
        }
    }, [refCode]);

    const submit = async (e) => {
        e.preventDefault();
        if (form.password.length < 6) return toast.error("Password must be at least 6 characters");
        if (!agreed) return toast.error("Please accept the Terms of Service and Privacy Policy to continue");
        setLoading(true);
        try {
            const stored = (() => { try { return localStorage.getItem("ef_ref"); } catch (e) { return null; } })();
            await register(form.name, form.email, form.password, refCode || stored || undefined);
            try { localStorage.removeItem("ef_ref"); } catch (e) { /* ignore */ }
            toast.success(refCode ? "Welcome! +30 trial days unlocked 🎁" : "Welcome aboard!");
            nav("/onboarding");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Signup failed");
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <SEO
                title="Sign up — Start your 7-day free trial — Energize.fit"
                description="Create your Energize.fit account and start a 7-day free trial of adaptive wellness guidance."
                path="/signup"
            />
            <AuthShell
                testid="signup-shell"
                heroImage={SIGNUP_HERO}
                heroQuote="Welcome to a different kind of wellness. No judgment — just progress."
                heroAttrib="Adaptive nutrition · Movement · Sleep · Stress"
                heading="Start your free trial."
                sub="7 days on us. Upload your blood report, get your AI health snapshot in minutes."
            >
                {refCode && (
                    <div className="mb-6 rounded-2xl border border-teal-200 bg-teal-50 p-4 flex items-start gap-3" data-testid="signup-ref-banner">
                        <Gift className="w-5 h-5 text-teal-600 mt-0.5" />
                        <div>
                            <p className="text-sm font-medium text-stone-900">Welcome via <span className="font-mono text-teal-700">{refCode}</span></p>
                            <p className="text-xs text-stone-600 mt-0.5">You'll get an extra 30 days on your free trial — 37 days total.</p>
                        </div>
                    </div>
                )}
                <form onSubmit={submit} className="space-y-5" data-testid="signup-form">
                    <Field label="Full name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testId="signup-name" required autoComplete="name" />
                    <Field label="Email" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testId="signup-email" required autoComplete="email" />
                    <div>
                        <label className="text-xs font-semibold uppercase tracking-[0.18em] text-stone-500 mb-2 block">Password</label>
                        <PasswordInput
                            value={form.password}
                            onChange={(v) => setForm({ ...form, password: v })}
                            required
                            autoComplete="new-password"
                            testId="signup-password"
                            placeholder="Min 8 characters"
                            minLength={8}
                            className="w-full bg-white border border-stone-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-teal-500/40 focus:border-teal-500 outline-none transition"
                        />
                    </div>
                    <label className="flex items-start gap-3 cursor-pointer select-none" data-testid="signup-consent-row">
                        <input
                            type="checkbox"
                            checked={agreed}
                            onChange={(e) => setAgreed(e.target.checked)}
                            data-testid="signup-consent-checkbox"
                            className="mt-1 w-4 h-4 rounded border-stone-300 text-teal-600 focus:ring-teal-500"
                        />
                        <span className="text-sm text-stone-600 leading-relaxed">
                            I agree to the{" "}
                            <Link to="/terms-of-service" target="_blank" className="text-stone-900 underline underline-offset-2 hover:text-teal-700">Terms of Service</Link>,{" "}
                            <Link to="/privacy-policy" target="_blank" className="text-stone-900 underline underline-offset-2 hover:text-teal-700">Privacy Policy</Link>, and{" "}
                            <Link to="/disclaimer" target="_blank" className="text-stone-900 underline underline-offset-2 hover:text-teal-700">Health Disclaimer</Link>.
                        </span>
                    </label>
                    <button type="submit" disabled={loading || !agreed} data-testid="signup-submit" className="w-full inline-flex items-center justify-center gap-2 bg-stone-900 text-white rounded-full px-6 py-4 font-medium hover:-translate-y-0.5 transition-transform disabled:opacity-60 disabled:cursor-not-allowed">
                        {loading ? "Creating…" : <>Create account <ArrowRight className="w-4 h-4" /></>}
                    </button>
                </form>
                <p className="text-sm text-stone-500 mt-8">
                    Already have an account? <Link to="/login" data-testid="signup-login-link" className="text-stone-900 font-medium underline underline-offset-4">Log in</Link>
                </p>
            </AuthShell>
        </>
    );
}

function Field({ label, value, onChange, type = "text", testId, required, autoComplete }) {
    return (
        <div>
            <label className="text-xs font-semibold uppercase tracking-[0.18em] text-stone-500 mb-2 block">{label}</label>
            <input
                type={type}
                required={required}
                autoComplete={autoComplete}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                data-testid={testId}
                className="w-full bg-white border border-stone-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-teal-500/40 focus:border-teal-500 outline-none transition"
            />
        </div>
    );
}
